import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DetectMethodChanges } from 'projects/shared-components/utils';
import { MessageBusService } from 'projects/shared-components/message-bus.service';
import { CollarsSessionHistoryComponent } from '../collars-session-history.component';
import { CollarsSessionHistoryScope } from '../collars-session-history-scope';

/**
 * Full-screen popup wrapper for the Collars Session P&L history dialog (clone of the ETS
 * session-history popup). Launched from the host-menu via show(null) — the unscoped browser — or
 * summoned over the 'ShowCollarsSessionHistory' bus topic by a Collars panel bucket's context menu
 * with a CollarsSessionHistoryScope payload (owner request 2026-06-11). One singleton instance,
 * hosted by the host-menu.
 */
@Component({
   selector: 'ets-collars-session-history-popup',
   template: `
   <dx-popup width="95%" height="65%"
            [showTitle]="true"
            [title]="title"
            [closeOnOutsideClick]="false"
            [resizeEnabled]="true"
            [dragEnabled]="true"
            [visible]="isVisible"
            (onShown)="onShown()"
            (onHidden)="onHidden()"
            [fullScreen]="true">
      <div *dxTemplate="let data of 'content'">
         <ets-collars-session-history></ets-collars-session-history>
      </div>
   </dx-popup>
   `,
   changeDetection: ChangeDetectionStrategy.OnPush
})
export class CollarsSessionHistoryPopupComponent implements OnInit, OnDestroy {

   constructor(
      private readonly _changeDetector: ChangeDetectorRef,
      private readonly _messageBus: MessageBusService
   ) {
   }

   @ViewChild(CollarsSessionHistoryComponent) sessionHistoryComponent: CollarsSessionHistoryComponent;
   private _config: CollarsSessionHistoryScope;
   private readonly _unsubscriber = new Subject<any>();

   isVisible = false;

   get title(): string {
      if (!this._config) { return 'Session History'; }
      const noun = this._config.level === 0 ? 'Portfolio' : (this._config.level === 1 ? 'Combo' : 'Group');
      return `Session History — ${noun} "${this._config.bucketName}"`;
   }

   ngOnInit() {
      this._messageBus.of<CollarsSessionHistoryScope>('ShowCollarsSessionHistory')
         .pipe(takeUntil(this._unsubscriber))
         .subscribe(msg => this.show(msg.payload));
   }

   ngOnDestroy() {
      this._unsubscriber.next();
      this._unsubscriber.complete();
   }

   @DetectMethodChanges()
   onShown() {
      if (this.sessionHistoryComponent) {
         this.sessionHistoryComponent.onShown(this._config);
      }
   }

   @DetectMethodChanges()
   onHidden() {
      this.isVisible = false;
      this._config = null;   // a later host-menu open must not inherit a stale bucket scope
      if (this.sessionHistoryComponent) {
         this.sessionHistoryComponent.onHidden();
      }
   }

   @DetectMethodChanges()
   show(config: CollarsSessionHistoryScope) {
      this._config = config;
      this.isVisible = true;
   }
}
